import { primary } from 'src/theme/palette';

export const CHANNEL = { PHONE: 'phone', WHATSAPP: 'whatsapp' };

export const OUTCOME = {
  ANSWERED: 'answered',
  MISSED: 'missed',
  BUSY: 'busy',
  FAILED: 'failed',
  VOICEMAIL: 'voicemail',
  UNKNOWN: 'unknown',
};

// Call purpose taxonomy (Phase B). `color` is a theme palette key — the chip
// tints from src/theme/palette.js, never a hardcoded hex.
export const PURPOSE = {
  EMERGENCY: { code: 'EMERGENCY', label: 'Emergency', color: 'error' },
  EXISTING_APPOINTMENT: { code: 'EXISTING_APPOINTMENT', label: 'Existing appointment', color: 'info' },
  NEW_ENQUIRY: { code: 'NEW_ENQUIRY', label: 'New enquiry', color: 'success' },
  GENERAL_ENQUIRY: { code: 'GENERAL_ENQUIRY', label: 'General enquiry', color: 'warning' },
  OTHER: { code: 'OTHER', label: 'Other', color: 'grey' },
};
export const PURPOSE_OPTIONS = Object.values(PURPOSE);

// ── Voice integration (provider-neutral Call rows) ──────────────────────────
// Optional fields the backend adds to each call row once the provider-neutral
// Call model lands (Voice Integration Spec, "Call Logs read model"). Rows from
// an older backend simply lack them; the UI hides the related columns,
// filters and breakdown until at least one loaded row carries a value.
export const PROVIDER = {
  twilio: { key: 'twilio', label: 'Twilio' },
  whatsapp: { key: 'whatsapp', label: 'WhatsApp' },
  nec_univerge_blue: { key: 'nec_univerge_blue', label: 'NEC UNIVERGE BLUE' },
};
export const providerLabel = (key) => PROVIDER[key]?.label || (key ? String(key).replace(/_/g, ' ') : '');

// Why an inbound call went unanswered. Keys are the vendor contract values;
// `short_missed`, `pre_queue_dropped` and `capacity_missed` are the
// VoiceStack-style distinctions the reducer may infer from ring duration.
export const MISSED_REASON = {
  auto_attendant: { key: 'auto_attendant', label: 'Automated line, nobody answered', color: 'info' },
  voicemail: { key: 'voicemail', label: 'Went to voicemail', color: 'info' },
  no_answer: { key: 'no_answer', label: 'Rang out', color: 'warning' },
  abandoned: { key: 'abandoned', label: 'Caller hung up', color: 'warning' },
  short_missed: { key: 'short_missed', label: 'Caller hung up quickly', color: 'warning' },
  pre_queue_dropped: { key: 'pre_queue_dropped', label: 'Hung up during the greeting', color: 'info' },
  busy: { key: 'busy', label: 'Line busy', color: 'error' },
  capacity_missed: { key: 'capacity_missed', label: 'All lines busy', color: 'error' },
  out_of_hours: { key: 'out_of_hours', label: 'Out of hours', color: 'grey' },
};
export const MISSED_REASON_OPTIONS = Object.values(MISSED_REASON);
export const missedReasonLabel = (key) => MISSED_REASON[key]?.label || (key ? String(key).replace(/_/g, ' ') : '');

// Where a missed call sits in the practice's call-back effort.
export const FOLLOW_UP_STATE = {
  unresponded: { key: 'unresponded', label: 'Awaiting call-back', color: 'error', open: true },
  first_attempt: { key: 'first_attempt', label: '1st attempt', color: 'warning', open: true },
  second_attempt: { key: 'second_attempt', label: '2nd attempt', color: 'warning', open: true },
  third_attempt: { key: 'third_attempt', label: '3rd attempt', color: 'warning', open: true },
  responded: { key: 'responded', label: 'Responded', color: 'success', open: false },
  ignored: { key: 'ignored', label: 'Ignored', color: 'grey', open: false },
};
export const FOLLOW_UP_STATE_OPTIONS = Object.values(FOLLOW_UP_STATE);
export const followUpStateLabel = (key) => FOLLOW_UP_STATE[key]?.label || (key ? String(key).replace(/_/g, ' ') : '');

// To call back covers owed calls missed in the last 7 days; older ones are Expired (server
// apps/voice/queue.py QUEUE_LOOKBACK). One figure for every label that names it.
export const CALL_BACK_WINDOW_DAYS = 7;
export const EXPIRED_HINT = `Not called back within ${CALL_BACK_WINDOW_DAYS} days. Restore it from To call back → Expired.`;
// An open call-back on a call missed more than CALL_BACK_WINDOW_DAYS ago: All calls says
// Expired, as To call back does. Tried 3 times stays "3rd attempt" (Couldn't reach there).
const EXPIRES = ['unresponded', 'first_attempt', 'second_attempt'];
export function callBackExpired(row, now = Date.now()) {
  if (!row || !EXPIRES.includes(row.followUpState) || !row.timestamp) return false;
  const started = new Date(row.timestamp).getTime();
  return Number.isFinite(started) && now - started > CALL_BACK_WINDOW_DAYS * 24 * 60 * 60 * 1000;
}

// Duration column: a missed call's length is how long the caller waited, not a conversation.
const clock = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
export function durationText(row) {
  if (row?.durationSec == null) return '—';
  return row.outcome === OUTCOME.MISSED ? `Waited ${clock(row.durationSec)}` : clock(row.durationSec);
}

// The global `button { text-transform: capitalize !important }` (App.css) turns "Not a
// patient" into "Not A Patient". Put this on a page's root (and on its dialogs, which
// render outside it) to keep labels as written; same rule as the Phone system page.
// ButtonBase covers headings that are buttons (Team's foldable sections).
export const SENTENCE_CASE = { '& .MuiButton-root, & .MuiButtonBase-root, & .MuiTab-root, & .MuiChip-label, & .MuiMenuItem-root': { textTransform: 'none !important' } };

// One accent for every Call Logs card (CU-869fnzh3x): the theme's primary colour, as the
// Booked page uses one accent for its cards. Urgency stays in the row chips (Overdue, Due
// today), never in the card colours.
export const CALL_LOGS_ACCENT = primary.main;

// Dates as the lead tables show them (Booked: "Fri 06 Nov 2026" / "At 03:00 PM"), in UK time.
export const APP_DAY_FORMAT = 'ddd DD MMM YYYY';
export const APP_TIME_FORMAT = 'hh:mm A';

// The Actions column: one fixed width on To call back and All calls (main button + "⋯").
export const ACTIONS_WIDTH = 190;
